import { DataTable } from "@/components/DataTable";
import { FilterChip } from "@/components/FilterChip";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  api,
  type AdminWaitlistEntry,
  type WaitlistStatus,
  type WaitlistStatusFilter,
} from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { toast } from "sonner";

const STATUS_OPTIONS: Array<{ key: WaitlistStatusFilter; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

function StatusBadge({ status }: { status: WaitlistStatus }) {
  if (status === "approved") return <Badge variant="default">Approved</Badge>;
  if (status === "rejected")
    return <Badge variant="outline" className="text-muted-foreground">Rejected</Badge>;
  return <Badge variant="secondary">Pending</Badge>;
}

// Per-row actions. Only pending entries are actionable; reviewed rows show when
// they were actioned. Gated on _permissions.
function RowActions({
  entry,
  canApprove,
  canReject,
}: {
  entry: AdminWaitlistEntry;
  canApprove: boolean;
  canReject: boolean;
}) {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin-waitlist"] });

  const approve = useMutation({
    mutationFn: () => api.admin.waitlist.approve(entry.id),
    onSuccess: () => {
      toast.success(`Approved — emailed ${entry.orgLabel} to finish setup`);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Approve failed"),
  });
  const addToOrg = useMutation({
    mutationFn: () => api.admin.waitlist.addToOrg(entry.id, entry.matchedOrg!.id),
    onSuccess: () => {
      toast.success(
        `Added ${entry.requestedByEmail ?? "user"} to ${entry.matchedOrg?.name ?? "existing team"}`,
      );
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Add to org failed"),
  });
  const reject = useMutation({
    mutationFn: () => api.admin.waitlist.reject(entry.id),
    onSuccess: () => {
      toast.success(`Rejected ${entry.orgLabel}`);
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Reject failed"),
  });

  if (entry.status !== "pending") {
    return (
      <span className="text-xs text-muted-foreground">
        {entry.reviewedAt ? formatDate(entry.reviewedAt) : "—"}
      </span>
    );
  }

  const busy = approve.isPending || addToOrg.isPending || reject.isPending;
  return (
    <div className="flex justify-end gap-2">
      {canApprove && (
        <Button size="sm" onClick={() => approve.mutate()} disabled={busy} loading={approve.isPending}>
          Approve
        </Button>
      )}
      {canApprove && entry.matchedOrg && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => addToOrg.mutate()}
          disabled={busy}
          loading={addToOrg.isPending}
        >
          Add to team
        </Button>
      )}
      {canReject && (
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          onClick={() => reject.mutate()}
          disabled={busy}
          loading={reject.isPending}
        >
          Reject
        </Button>
      )}
    </div>
  );
}

export function AdminWaitlistPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<WaitlistStatusFilter>("pending");

  const listQuery = useQuery({
    queryKey: ["admin-waitlist", status],
    queryFn: () => api.admin.waitlist.list(status),
  });
  const settingsQuery = useQuery({
    queryKey: ["admin-waitlist-settings"],
    queryFn: () => api.admin.waitlist.settings(),
  });

  const perms = listQuery.data?._permissions ?? null;
  const canApprove = !!perms?.canApprove;
  const canReject = !!perms?.canReject;
  const canToggle = !!perms?.canToggle;

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.admin.waitlist.setEnabled(enabled),
    onSuccess: (res) => {
      toast.success(
        res.enabled ? "Waitlist enabled — new signups will wait for approval" : "Waitlist disabled",
      );
      queryClient.invalidateQueries({ queryKey: ["admin-waitlist-settings"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to update"),
  });

  const columns: ColumnDef<AdminWaitlistEntry>[] = [
    {
      accessorKey: "orgLabel",
      header: "Signup",
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="min-w-0">
            <div className="font-medium truncate">{r.requestedByEmail ?? r.orgLabel}</div>
            {r.orgWebsite && (
              <div className="text-xs text-muted-foreground truncate">{r.orgWebsite}</div>
            )}
            {r.matchedOrg && (
              <div className="text-xs text-amber-600 dark:text-amber-500">
                ⚠ matches existing team {r.matchedOrg.name ?? r.matchedOrg.id.slice(0, 8)} (via{" "}
                {r.matchedOrg.via})
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "agentType",
      header: "Type",
      cell: ({ row }) => (
        <Badge variant="secondary" className="text-[10px]">
          {row.original.agentType}
        </Badge>
      ),
    },
    {
      accessorKey: "submittedAt",
      header: "Submitted",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{formatDate(row.original.submittedAt)}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <RowActions entry={row.original} canApprove={canApprove} canReject={canReject} />
      ),
    },
  ];

  const currentLabel = STATUS_OPTIONS.find((o) => o.key === status)?.label ?? "Pending";

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Waitlist"
          subtitle="Self-serve signups awaiting FDE approval before a Boo is provisioned."
        />
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Waitlist</span>
          <Switch
            checked={!!settingsQuery.data?.enabled}
            disabled={!canToggle || settingsQuery.isLoading || toggle.isPending}
            onCheckedChange={(v) => toggle.mutate(v)}
            aria-label="Toggle waitlist"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <FilterChip
          label="Status"
          value={currentLabel}
          options={STATUS_OPTIONS}
          onSelect={(k) => setStatus(k as WaitlistStatusFilter)}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => listQuery.refetch()}
          disabled={listQuery.isFetching}
        >
          {listQuery.isFetching ? "Loading…" : "Refresh"}
        </Button>
      </div>

      {listQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : listQuery.isError || !listQuery.data ? (
        <ErrorComponent error={listQuery.error ?? "Failed to load"} />
      ) : listQuery.data.data.length > 0 ? (
        <DataTable columns={columns} data={listQuery.data.data} />
      ) : (
        <p className="text-sm text-muted-foreground">
          {status === "pending" ? "No one is waiting right now." : `No ${status} entries.`}
        </p>
      )}
    </div>
  );
}
